import Button from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { type SendDatabaseCreatedViewEventData } from '@databases/analytics/src/types';
import Popup, { DefaultPopupContentContainer } from '@databases/atlaskit/Popup';
import { isMobile } from '@databases/modes';
import { type DatabaseView } from '@databases/types/database/view';
import useHandleOutsideClick from '@databases/utils/hooks/useHandleOutsideClick';
import { type ViewPanelSettingType } from '@databases/views/types';
import useView from '@databases/views/useView';
import React, { type FunctionComponent, useCallback, useRef } from 'react';
import styled from 'styled-components';

import ViewList from './ViewList';

const POPUP_WIDTH_MOBILE = 250;
const POPUP_WIDTH = 400;

const PopupContentContainer = styled(DefaultPopupContentContainer)`
  && > * {
    // ScrollableContainer InnerContainer
    padding: ${token('space.075', '6px')};
    margin-top: 0px;
  }
`;

const contentContainerStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  overflowX: 'hidden',
});

export interface ViewPickerProps {
  contentId: string;
  canEditViews: boolean;

  /** The current default view. If unspecified, the default view of the database will be used. */
  defaultView?: DatabaseView;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleAddView: (string: SendDatabaseCreatedViewEventData['source']) => void;
  openViewSettingsPanel: (activeViewSetting: ViewPanelSettingType | null) => void;
  showViewSettings?: boolean;
  tooltipContent?: string;
}

const ViewPicker: FunctionComponent<ViewPickerProps> = ({
  contentId,
  canEditViews,
  defaultView,
  isOpen,
  setIsOpen,
  handleAddView,
  openViewSettingsPanel,
  showViewSettings = true,
  tooltipContent,
}) => {
  const { view, setViewId } = useView();
  const viewPickerRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const onAddViewWithClose = useCallback(() => {
    setIsOpen(false);
    handleAddView('databaseViewPicker');
  }, [handleAddView, setIsOpen]);

  const onViewChangeWithClose = useCallback(
    (viewId: string) => {
      setViewId(viewId);
      setIsOpen(false);
    },
    [setViewId, setIsOpen]
  );

  useHandleOutsideClick(
    handleClose,
    viewPickerRef,
    [
      '[id="view-picker-popup-button"]',
      '[data-testid="view-actions-dropdown-menu--content"]',
    ],
    isOpen
  );

  const popupWidth = isMobile() ? POPUP_WIDTH_MOBILE : POPUP_WIDTH;

  return (
    <Popup
      testId="view-picker-overflow-menu"
      contentContainer={PopupContentContainer}
      isOpen={isOpen}
      onClose={handleClose}
      trigger={(triggerProps) => (
        <Tooltip
          testId="view.picker.dropdown.button.tooltip"
          content={isMobile() ? '' : tooltipContent}
        >
          <Button
            {...triggerProps}
            iconAfter={
              <ChevronDownIcon LEGACY_size="medium" spacing="spacious" label="" />
            }
            onClick={handleToggle}
            isSelected={isOpen}
            isDisabled={!contentId}
            appearance="subtle"
            testId="view-picker-popup-button"
            id="view-picker-popup-button"
          />
        </Tooltip>
      )}
      placement="bottom"
      content={() =>
        contentId && (
          <Box xcss={contentContainerStyles}>
            <ViewList
              contentId={contentId}
              value={view}
              onChange={onViewChangeWithClose}
              canEditViews={canEditViews}
              defaultView={defaultView}
              handleAddView={onAddViewWithClose}
              openViewSettingsPanel={openViewSettingsPanel}
              handleClose={handleClose}
              showViewSettings={showViewSettings}
              viewPickerRef={viewPickerRef}
            />
          </Box>
        )
      }
      width={popupWidth}
    />
  );
};

export default ViewPicker;
